import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";

/** Resolve stored invoice/document paths to a browser-openable URL. */
export function resolveStorageDocumentUrl(filePath: string | null | undefined): string | null {
  if (!filePath) return null;
  const raw = String(filePath).trim();
  if (!raw) return null;
  const match = raw.match(/documents\/invoices\/([^/?#]+)/);
  if (match) return `/storage/documents/invoices/${match[1]}`;
  if (raw.startsWith("/storage/")) return normalizeProductMediaUrlForBrowser(raw) ?? raw;
  if (raw.startsWith("documents/")) return `/storage/${raw}`;
  return normalizeProductMediaUrlForBrowser(raw) ?? raw;
}

function toAbsoluteUrl(pathOrUrl: string): string {
  try {
    return new URL(pathOrUrl, window.location.origin).href;
  } catch {
    return pathOrUrl;
  }
}

/**
 * Open the system Print dialog for a PDF/image at a storage URL.
 * Uses a temporary full-page iframe and iframe.contentWindow.print() —
 * this is what actually triggers the print dialog for PDFs in Chrome.
 */
export function openDocumentPrintView(
  filePathOrUrl: string,
  _options?: { title?: string },
): void {
  const resolved = resolveStorageDocumentUrl(filePathOrUrl) ?? filePathOrUrl.trim();
  if (!resolved) return;
  const url = toAbsoluteUrl(resolved);

  const id = "inventory-auto-print-frame";
  document.getElementById(id)?.remove();

  const iframe = document.createElement("iframe");
  iframe.id = id;
  iframe.setAttribute("title", "Print document");
  iframe.src = url;
  // Visible full-page layer briefly so the browser allows printing PDF content.
  iframe.style.cssText =
    "position:fixed;inset:0;width:100vw;height:100vh;border:0;z-index:2147483646;background:#fff;";

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    // Soft-hide; keep iframe alive while the print dialog uses it.
    window.setTimeout(() => {
      iframe.style.opacity = "0";
      iframe.style.pointerEvents = "none";
      iframe.style.zIndex = "-1";
    }, 300);
    window.setTimeout(() => iframe.remove(), 120_000);
  };

  iframe.addEventListener("load", () => {
    // Delay so Chrome's PDF plugin can finish mounting before print().
    window.setTimeout(triggerPrint, 800);
  });

  document.body.appendChild(iframe);

  // PDF plugin sometimes never fires load — force print once.
  window.setTimeout(triggerPrint, 2500);
}
